<script setup lang="ts">
import type { OnboardingPopupTaskRow } from '~/constants/onboarding-popup-tasks';
import TaskStatus from './TaskStatus.vue';

defineProps<{
	rows: OnboardingPopupTaskRow[];
	highlightedRowId: OnboardingPopupTaskRow['id'] | null;
}>();

const emit = defineEmits<{
	rowHover: [row: OnboardingPopupTaskRow, event: MouseEvent];
	rowFocus: [row: OnboardingPopupTaskRow];
	rowClick: [row: OnboardingPopupTaskRow, event: MouseEvent];
}>();
</script>

<template>
	<div
		data-test="onboarding-rows"
		class="flex flex-col gap-0.5"
	>
		<button
			v-for="row in rows"
			:key="row.id"
			type="button"
			data-test="onboarding-row"
			:data-row-id="row.id"
			:data-done="row.done"
			:data-locked="row.disabled"
			:data-highlighted="highlightedRowId === row.id ? '' : undefined"
			class="
				group/list relative z-0 flex w-full items-center gap-x-2 rounded-md px-2 py-1.5 text-start text-toned
				transition-colors outline-none
				before:absolute before:inset-px before:z-[-1] before:rounded-md before:transition-colors
				data-highlighted:text-highlighted
				data-highlighted:before:bg-elevated
			"
			:class="{ 'opacity-55': row.disabled }"
			@mouseenter="emit('rowHover', row, $event)"
			@focus="emit('rowFocus', row)"
			@pointerdown.stop
			@click.stop="emit('rowClick', row, $event)"
		>
			<div class="flex min-w-0 grow items-center gap-2">
				<TaskStatus :done="row.done" />
				<span class="truncate text-xs font-normal">
					{{ row.label }}
				</span>
			</div>

			<div
				class="
					grid w-6 shrink-0 place-items-center text-dimmed opacity-0 transition-opacity
					group-hover/list:opacity-100
					group-focus-visible/list:opacity-100
					group-data-highlighted/list:opacity-100
				"
			>
				<DIcon
					:name="row.disabled ? 'lucide:lock-keyhole' : 'lucide:chevron-right'"
					class="size-3"
				/>
			</div>
		</button>
	</div>
</template>
